import { Component } from '@angular/core';

export type Lang = 'en' | 'es';

@Component({
  selector: 'app-privacy',
  templateUrl: './privacy.component.html',
  styleUrls: ['./privacy.component.css']
})
export class PrivacyComponent {

  isNavOpen = false;
  lang: Lang = 'en';

  setLang(l: Lang) {
    this.lang = l;
  }

  get t() {
    return this.translations[this.lang];
  }

  translations = {
    en: {
      nav: {
        login: 'LOGIN',
        register: 'REGISTER NOW',
      },
      hero: {
        eyebrow: 'LEGAL & PLATFORM POLICIES',
        title: 'PRIVACY',
        titleHighlight: 'POLICY.',
        subtitle: 'Your privacy matters to us. This policy explains what information UMYO Español collects, how it is used, and the choices you have as a participating member of any UMYO Español ecosystem.'
      },
      meta: 'LAST UPDATED · UMYO ESPAÑOL PLATFORM POLICIES',
      sections: [
        {
          num: '01',
          title: 'Information We Collect',
          desc: 'To operate the Platform and its mission-specific ecosystems, the Company collects information you provide directly, information generated through your use of the Platform, and information obtained from third parties.',
          points: [
            '<strong>Information you provide.</strong> This includes account details such as your name, email address, phone number, profile information, payment information, and any content, messages, or materials you submit within an ecosystem.',
            '<strong>Information collected automatically.</strong> When you access the Platform, the Company may automatically collect device information, log data, IP address, browser type, approximate location, and usage activity such as pages viewed and features used.',
            '<strong>Information from third parties.</strong> The Company may receive information from payment processors, identity-verification providers, business partners, or publicly available sources in connection with your participation in the Platform.'
          ],
          highlight: 'UMYO Español collects only the information reasonably necessary to operate the Platform, verify eligibility for community programs, and maintain a safe environment across ecosystems.'
        },
        {
          num: '02',
          title: 'How We Use Your Information',
          desc: 'The Company uses collected information for purposes including:',
          list: [
            'Creating and administering member accounts and ecosystem access',
            'Operating, maintaining, and improving the Platform and its features',
            'Processing registrations, incentive applications, and eligibility verification',
            'Communicating with members about updates, programs, and policy changes',
            'Monitoring for fraud, abuse, and violations of Platform or ecosystem policies',
            'Complying with applicable laws, regulations, and legal requests'
          ],
          note: 'The Company does not sell members\' personal information to third parties for their own independent marketing purposes.'
        },
        {
          num: '03',
          title: 'Sharing and Disclosure of Information',
          desc: 'The Company may share information with:',
          list: [
            'Service providers who perform functions on the Company\'s behalf, such as hosting, payment processing, and identity verification',
            'Other members, to the extent required for participation within a shared ecosystem',
            'Business partners involved in the administration of hardship incentive or assistance programs',
            'Regulators, law enforcement, or other parties when required by law or to protect the rights, safety, or property of the Company or its members',
            'A successor entity in connection with a merger, acquisition, or sale of assets'
          ],
          highlight: 'Service providers and partners are permitted to use member information only as needed to perform their function for the Platform, and are contractually obligated to safeguard that information.'
        },
        {
          num: '04',
          title: 'Cookies and Tracking Technologies',
          desc: 'The Platform uses cookies and similar tracking technologies to recognize returning members, remember preferences, analyze traffic, and improve overall functionality.',
          points: [
            'Members may adjust browser settings to refuse or limit cookies; however, doing so may affect the availability of certain features or ecosystem functions.',
            'The Company does not knowingly permit third parties to use tracking technologies on the Platform for purposes unrelated to operating and improving the Platform.'
          ]
        },
        {
          num: '05',
          title: 'Data Security',
          desc: 'The Company implements administrative, technical, and physical safeguards designed to protect member information against unauthorized access, alteration, disclosure, or destruction.',
          points: [
            'No method of transmission or storage is completely secure. While the Company works to protect member information, it cannot guarantee absolute security, and members share responsibility for safeguarding their own account credentials.'
          ],
          highlight: 'Members should promptly report any suspected unauthorized access to their account or suspicious Platform activity.'
        },
        {
          num: '06',
          title: 'Your Rights and Choices',
          desc: 'Depending on your location, you may have rights regarding your personal information, including the right to:',
          list: [
            'Request access to the personal information the Company holds about you',
            'Request correction of inaccurate or incomplete information',
            'Request deletion of your personal information, subject to legal and operational limitations',
            'Opt out of certain communications or marketing messages',
            'Withdraw consent where processing is based on consent'
          ],
          note: 'Requests may be submitted through your account settings or by contacting the Company directly. The Company will respond within a reasonable timeframe as required by applicable law.'
        },
        {
          num: '07',
          title: 'Children\'s Privacy',
          desc: 'The Platform is not directed to individuals under the age of eighteen (18), and the Company does not knowingly collect personal information from minors.',
          points: [
            'If the Company becomes aware that it has inadvertently collected information from a minor, it will take reasonable steps to delete that information from its records.'
          ]
        },
        {
          num: '08',
          title: 'Changes to This Policy and Contact',
          desc: 'The Company may update this Privacy Policy from time to time to reflect changes in its practices, the Platform, or applicable law. Material changes will be communicated through the Platform or by other reasonable means.',
          points: [
            'Continued use of the Platform following any update constitutes acceptance of the revised Privacy Policy.'
          ],
          highlight: 'Questions, concerns, or requests regarding this Privacy Policy may be directed to the Company through the contact options available on the Platform.'
        }
      ],
      footer: {
        tagline: 'FOR US. BY US. POWERED BY UMYO.',
        quickLinks: 'QUICK LINKS',
        resources: 'RESOURCES',
        connect: 'LET\'S CONNECT',
        copyright: '© 2026 UMYO Español Directory. All Rights Reserved.'
      }
    },
    es: {
      nav: {
        login: 'INICIAR SESIÓN',
        register: 'REGISTRARSE AHORA',
      },
      hero: {
        eyebrow: 'POLÍTICAS LEGALES Y DE LA PLATAFORMA',
        title: 'PRIVACIDAD',
        titleHighlight: 'POLÍTICA.',
        subtitle: 'Tu privacidad es importante para nosotros. Esta política explica qué información recopila UMYO Español, cómo se utiliza y las opciones que tienes como miembro participante de cualquier ecosistema UMYO Español.'
      },
      meta: 'ÚLTIMA ACTUALIZACIÓN · POLÍTICAS DE LA PLATAFORMA UMYO ESPAÑOL',
      sections: [
        {
          num: '01',
          title: 'Información que Recopilamos',
          desc: 'Para operar la Plataforma y sus ecosistemas específicos, la Compañía recopila información que usted proporciona directamente, información generada a través de su uso de la Plataforma e información obtenida de terceros.',
          points: [
            '<strong>Información que usted proporciona.</strong> Esto incluye detalles de la cuenta como su nombre, dirección de correo electrónico, número de teléfono, información de perfil, información de pago y cualquier contenido, mensaje o material que envíe dentro de un ecosistema.',
            '<strong>Información recopilada automáticamente.</strong> Cuando accede a la Plataforma, la Compañía puede recopilar automáticamente información del dispositivo, datos de registro, dirección IP, tipo de navegador, ubicación aproximada y actividad de uso como páginas vistas y funciones utilizadas.',
            '<strong>Información de terceros.</strong> La Compañía puede recibir información de procesadores de pagos, proveedores de verificación de identidad, socios comerciales o fuentes disponibles públicamente en relación con su participación en la Plataforma.'
          ],
          highlight: 'UMYO Español recopila solo la información razonablemente necesaria para operar la Plataforma, verificar la elegibilidad para programas comunitarios y mantener un entorno seguro en todos los ecosistemas.'
        },
        {
          num: '02',
          title: 'Cómo Usamos Su Información',
          desc: 'La Compañía utiliza la información recopilada para fines que incluyen:',
          list: [
            'Crear y administrar cuentas de miembros y acceso al ecosistema',
            'Operar, mantener y mejorar la Plataforma y sus características',
            'Procesar registros, solicitudes de incentivos y verificación de elegibilidad',
            'Comunicarse con los miembros sobre actualizaciones, programas y cambios de políticas',
            'Monitorear fraudes, abusos y violaciones de las políticas de la Plataforma o del ecosistema',
            'Cumplir con las leyes, regulaciones y solicitudes legales aplicables'
          ],
          note: 'La Compañía no vende la información personal de los miembros a terceros para sus propios fines de marketing independientes.'
        },
        {
          num: '03',
          title: 'Intercambio y Divulgación de Información',
          desc: 'La Compañía puede compartir información con:',
          list: [
            'Proveedores de servicios que realizan funciones en nombre de la Compañía, como alojamiento, procesamiento de pagos y verificación de identidad',
            'Otros miembros, en la medida necesaria para la participación dentro de un ecosistema compartido',
            'Socios comerciales involucrados en la administración de programas de incentivos o asistencia por dificultades',
            'Reguladores, fuerzas del orden u otras partes cuando lo exija la ley o para proteger los derechos, la seguridad o la propiedad de la Compañía o sus miembros',
            'Una entidad sucesora en relación con una fusión, adquisición o venta de activos'
          ],
          highlight: 'Los proveedores de servicios y socios solo pueden usar la información de los miembros según sea necesario para realizar su función para la Plataforma, y están contractualmente obligados a salvaguardar esa información.'
        },
        {
          num: '04',
          title: 'Cookies y Tecnologías de Seguimiento',
          desc: 'La Plataforma utiliza cookies y tecnologías de seguimiento similares para reconocer a los miembros que regresan, recordar preferencias, analizar el tráfico y mejorar la funcionalidad general.',
          points: [
            'Los miembros pueden ajustar la configuración del navegador para rechazar o limitar las cookies; sin embargo, hacerlo puede afectar la disponibilidad de ciertas funciones o funciones del ecosistema.',
            'La Compañía no permite conscientemente que terceros utilicen tecnologías de seguimiento en la Plataforma para fines no relacionados con la operación y mejora de la Plataforma.'
          ]
        },
        {
          num: '05',
          title: 'Seguridad de los Datos',
          desc: 'La Compañía implementa salvaguardas administrativas, técnicas y físicas diseñadas para proteger la información de los miembros contra acceso no autorizado, alteración, divulgación o destrucción.',
          points: [
            'Ningún método de transmisión o almacenamiento es completamente seguro. Si bien la Compañía trabaja para proteger la información de los miembros, no puede garantizar la seguridad absoluta, y los miembros comparten la responsabilidad de salvaguardar sus propias credenciales de cuenta.'
          ],
          highlight: 'Los miembros deben informar de inmediato cualquier sospecha de acceso no autorizado a su cuenta o actividad sospechosa en la Plataforma.'
        },
        {
          num: '06',
          title: 'Sus Derechos y Opciones',
          desc: 'Dependiendo de su ubicación, es posible que tenga derechos con respecto a su información personal, incluido el derecho a:',
          list: [
            'Solicitar acceso a la información personal que la Compañía tiene sobre usted',
            'Solicitar la corrección de información inexacta o incompleta',
            'Solicitar la eliminación de su información personal, sujeto a limitaciones legales y operativas',
            'Optar por no recibir ciertas comunicaciones o mensajes de marketing',
            'Retirar el consentimiento cuando el procesamiento se base en el consentimiento'
          ],
          note: 'Las solicitudes pueden enviarse a través de la configuración de su cuenta o comunicándose con la Compañía directamente. La Compañía responderá dentro de un plazo razonable según lo exija la ley aplicable.'
        },
        {
          num: '07',
          title: 'Privacidad de los Niños',
          desc: 'La Plataforma no está dirigida a personas menores de dieciocho (18) años, y la Compañía no recopila conscientemente información personal de menores.',
          points: [
            'Si la Compañía se da cuenta de que ha recopilado inadvertidamente información de un menor, tomará medidas razonables para eliminar esa información de sus registros.'
          ]
        },
        {
          num: '08',
          title: 'Cambios a Esta Política y Contacto',
          desc: 'La Compañía puede actualizar esta Política de Privacidad de vez en cuando para reflejar cambios en sus prácticas, la Plataforma o la ley aplicable. Los cambios materiales se comunicarán a través de la Plataforma u otros medios razonables.',
          points: [
            'El uso continuado de la Plataforma después de cualquier actualización constituye la aceptación de la Política de Privacidad revisada.'
          ],
          highlight: 'Preguntas, inquietudes o solicitudes relacionadas con esta Política de Privacidad pueden dirigirse a la Compañía a través de las opciones de contacto disponibles en la Plataforma.'
        }
      ],
      footer: {
        tagline: 'PARA NOSOTROS. POR NOSOTROS. POTENCIADO POR UMYO.',
        quickLinks: 'ENLACES RÁPIDOS',
        resources: 'RECURSOS',
        connect: 'CONECTEMOS',
        copyright: '© 2026 Directorio UMYO Español. Todos los derechos reservados.'
      }
    }
  };
}